<template>
  <ModalBase
    v-slot="{ close, confirm }"
    v-bind="$attrs"
  >
    <NewBsBox
      :bs="{
        'cmp.modal': 'container',
        'cmp.modal.common': 'sized',
        py: 24,
        ...(scrollContent
          ? {
            overflow: 'auto',
            maxHeight: '90vh',
          }
          : {}),
        ...bs,
      }"
      :context="$props"
      role="dialog"
    >
      <template v-if="type === 'dialog'">
        <NewBsBox
          :context="$props"
          :bs="{ 'cmp.modal': 'header' }"
          class="header"
        >
          <!-- @slot 标题 -->
          <slot name="title">
            {{ title }}
          </slot>
        </NewBsBox>
      </template>
      <template v-else>
        <NewBsBox
          :context="$props"
          :bs="{
            'cmp.modal': 'header',
            position: 'sticky',
            top: 0,
            bg: 'white',
          }"
          class="header"
        >
          <div style="width: 100%">
            <slot name="title">
              {{ title }}
            </slot>
          </div>
          <BeerIcon
            v-if="!hideCloseIcon"
            :bs="{ cursor: 'pointer' }"
            :grid-size="16"
            :icon-path="closeIcon || CROSS_PATH_16"
            color="mute"
            @click="close"
          />
        </NewBsBox>

        <NewBsBox
          :bs="{
            'cmp.modal': 'content',
            ...(scrollContent
              ? {
                maxHeight: '65vh',
                overflow: 'auto',
              }
              : {}),
            ...contentBs,
          }"
          class="content"
        >
          <!--
              @slot 内容
              @binding {func} close 离开函数，会触发 update:show 事件
              @binding {func} confirm
            -->
          <slot
            :close="close"
            :confirm="confirm"
          />
        </NewBsBox>
      </template>

      <NewBsBox
        :bs="{ 'cmp.modal': 'footer' }"
        :context="$props"
        class="footer"
      >
        <!--
            @slot modal 底部，默认确定、删除
            @binding {func} close 离开函数，会触发 update:show 事件
            @binding {func} confirm
          -->
        <slot
          :close="close"
          :confirm="confirm"
          name="footer"
        >
          <BeerButton
            v-if="cancelText"
            variant="secondary"
            v-bind="cancelProps"
            @click="close"
          >
            {{ cancelText }}
          </BeerButton>
          <BeerButton
            v-if="confirmText"
            :disabled="disableConfirm"
            :bs="{ mr: 0 }"
            v-bind="confirmProps"
            @click="confirm"
          >
            {{ confirmText }}
          </BeerButton>
        </slot>
      </NewBsBox>
    </NewBsBox>
  </ModalBase>
</template>

<script lang="ts">
  import { PropType, defineComponent } from 'vue'
  import NewBsBox from '@xhs/yam-beer/cmp/primitive/NewBsBox'
  import BeerIcon from '@xhs/yam-beer/cmp/common/Icon/Icon.vue'
  import BeerButton from '@xhs/yam-beer/cmp/common/Button/Button.vue'
  import { CROSS_PATH_16 } from '@xhs/yam-beer/cmp/constants/builtinIcon'
  import { BeerStyleObj } from '../../../core'
  import ModalBase from './ModalBase.vue'

  /**
   * 这个是固定 Header / Footer 的一种样式上的扩展
   * 如果想要写自己的 modal 可以从 BeerModalBase 扩展
   *
   * @see 继承BeerModalBase #sfc-BeerModalBase
   *
   */
  export default defineComponent({
    name: 'BeerModal',
    components: {
      ModalBase,
      BeerIcon,
      BeerButton,
      NewBsBox,
    },
    props: {
      /**
       * Modal 容器 BS 样式
       * @ignore
       */
      bs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 内容区域 inline 样式
       * @ignore
       */
      contentBs: {
        type: Object as PropType<BeerStyleObj>,
        default: () => ({}),
      },
      /**
       * 是否需要内容区域 scrollable
       * - true 的时候 popover 可能会在滚动的区域里面而不可见
       * - 设置为 false，如果你的 content 很小
       */
      scrollContent: {
        type: Boolean,
        default: true,
      },
      /**
       * modal的标题
       */
      title: {
        type: String,
        default: '',
      },
      /**
       * @type {modal|dialog}
       * dialog 有特殊样式，如：无关闭图标、标题
       */
      type: {
        type: String as PropType<'dialog' | 'modal'>,
        default: 'modal',
        validator: (val: string) => ['dialog', 'modal'].includes(val),
      },
      /**
       * size= s，m, l, xl,
       * s=400px, m=480px
       * l=640px, xl=800px
       * 也可以size="500px"使用
       */
      size: {
        type: String,
        default: 's',
      },
      /**
       * 是否隐藏closeIcon
       */
      hideCloseIcon: {
        type: Boolean,
        default: false,
      },
      closeIcon: {
        type: String,
        required: false,
      },
      /**
       * footer按钮位置，
       * 可选：left、center、right
       */
      footerPosition: {
        type: String as PropType<'left' | 'center' | 'right'>,
        default: 'right',
        validator: (val: string) => ['left', 'center', 'right'].includes(val),
      },
      /**
       * 关闭按钮文本
       */
      cancelText: {
        type: String,
        default: '取消',
      },
      /**
       * 关闭按钮 props
       */
      cancelProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 确认按钮文本
       */
      confirmText: {
        type: String,
        default: '确定',
      },
      /**
       * 确认按钮 props
       */
      confirmProps: {
        type: Object,
        default: () => ({}),
      },
      /**
       * 是否禁用确认按钮
       */
      disableConfirm: {
        type: Boolean,
        default: false,
      },
    },
    setup() {
      return {
        CROSS_PATH_16,
      }
    },
  })
</script>
